import pshLogo from '../../assets/psh-logo.png';
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import toastManager from '../../utils/toastManager';
import {
  Search,
  ShoppingCart,
  User,
  X,
  Sun,
  Moon,
  Heart,
  Globe,
  Gift,
  LogOut,
  Package,
  ChevronDown,
  ChevronRight,
  Send,
  Download,
  Bell,
  Gamepad2,
  PartyPopper,
  FileCode,
  Sparkles,
  Coins
} from 'lucide-react';

import { toggleTheme, setActiveTheme } from '../../store/slices/uiSlice';
import { logoutUser, updateUserProfile } from '../../store/slices/authSlice';
import { LANGUAGES, LANGUAGE_MAP, LANGUAGE_FLAGS } from '../../utils/constants';
import { selectCartItemCount, toggleCart } from '../../store/slices/cartSlice';
import { selectWishlistItemCount } from '../../store/slices/wishlistSlice';
import { setSearchQuery } from '../../store/slices/productsSlice';

import SearchDropdown from '../common/SearchDropdown';
import PointsHistoryModal from '../modals/PointsHistoryModal';
import PointsTopUpModal from '../modals/PointsTopUpModal';
import { subscribeToThemeSettings } from '../../services/firebase';
import { getAuthRedirectPath } from '../../utils/authRedirect';
import { HalloweenPumpkinIcon, HalloweenBatIcon, HalloweenSpiderWebIcon } from '../common/halloween/HalloweenIcons';

const Header = ({ unreadNotifications = 0 }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchFocus, setSearchFocus] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [pointsHistoryOpen, setPointsHistoryOpen] = useState(false);
  const [pointsTopUpOpen, setPointsTopUpOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [adminTheme, setAdminTheme] = useState('default');
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const searchRef = useRef(null);
  const userMenuRef = useRef(null);
  const langMenuRef = useRef(null);

  const { theme, activeTheme } = useSelector((state) => state.ui);
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { settings } = useSelector((state) => state.settings);
  const { searchQuery } = useSelector((state) => state.products);
  const cartItemCount = useSelector(selectCartItemCount);
  const wishlistItemCount = useSelector(selectWishlistItemCount);

  const getFestivalIcon = () => {
    const current = activeTheme !== 'default' ? activeTheme : adminTheme;
    const isActive = activeTheme !== 'default';
    const bounceClass = isActive ? 'animate-bounce' : 'grayscale opacity-70';
    switch (current) {
      case 'halloween':
        return <HalloweenPumpkinIcon className={`w-5 h-5 transition-all ${isActive ? 'animate-bounce drop-shadow-[0_0_8px_rgba(249,115,22,0.9)]' : 'grayscale opacity-70'}`} />;
      case 'christmas':
        return <span className={`text-lg leading-none transition-all ${isActive ? 'animate-bounce drop-shadow-[0_0_8px_rgba(56,189,248,0.9)]' : 'grayscale opacity-70'}`}>🎄</span>;
      case 'newyear':
        return <span className={`text-lg leading-none transition-all ${isActive ? 'animate-bounce drop-shadow-[0_0_8px_rgba(245,158,11,0.8)]' : 'grayscale opacity-70'}`}>🥂</span>;
      case 'valentines':
        return <span className={`text-lg leading-none transition-all ${isActive ? 'animate-bounce drop-shadow-[0_0_8px_rgba(244,63,94,0.8)]' : 'grayscale opacity-70'}`}>💘</span>;
      case 'diwali':
        return <span className={`text-lg leading-none transition-all ${isActive ? 'animate-bounce drop-shadow-[0_0_8px_rgba(245,158,11,0.9)]' : 'grayscale opacity-70'}`}>🪔</span>;
      case 'blackfriday':
        return <span className={`text-lg leading-none transition-all ${isActive ? 'animate-bounce drop-shadow-[0_0_8px_rgba(6,182,212,0.9)]' : 'grayscale opacity-70'}`}>⚡</span>;
      case 'eid':
        return <span className={`text-lg leading-none transition-all ${isActive ? 'animate-bounce drop-shadow-[0_0_8px_rgba(16,185,129,0.9)]' : 'grayscale opacity-70'}`}>🌙</span>;
      case 'summersale':
        return <span className={`text-lg leading-none transition-all ${isActive ? 'animate-bounce drop-shadow-[0_0_8px_rgba(249,115,22,0.9)]' : 'grayscale opacity-70'}`}>☀️</span>;
      default:
        return <PartyPopper className={`w-5 h-5 ${bounceClass}`} />;
    }
  };

  const getThemeHeaderActionClasses = () => {
    if (activeTheme !== 'default') {
      return 'bg-white/10 dark:bg-white/10 text-white border border-white/20 hover:bg-white/20 shadow-sm backdrop-blur-md transition-all hover:scale-105';
    }
    return 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors';
  };

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);

  }, []);

  // Listen to Admin Theme changes
  useEffect(() => {
    const unsubscribe = subscribeToThemeSettings((settings) => {
      setAdminTheme(settings?.activeTheme || 'default');
    });
    return () => unsubscribe();
  }, []);

  // Close dropdowns when clicking outside (desktop only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchFocus(false);
      }
      if (!isMobile && userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setLangMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  // Close menus when route changes
  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile user menu is open
  useEffect(() => {
    if (isMobile && userMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, userMenuOpen]);

  const handleSearch = (query) => {
    dispatch(setSearchQuery(query));
    setSearchFocus(false);
    if (query.trim()) {
      navigate('/products');
    }
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    setUserMenuOpen(false);
    navigate('/');
  };

  const handleMenuItemClick = () => {
    setUserMenuOpen(false);
  };

  const handleUserMenuToggle = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  const handleWishlistClick = (e) => {
    if (!isAuthenticated) {
      e.preventDefault();
      toastManager.error('Please sign in to view your wishlist');
      setTimeout(() => navigate(getAuthRedirectPath('/wishlist')), 1000);
    } else {
      navigate('/wishlist');
    }
  };

  const handleCartClick = (e) => {
    e.preventDefault();
    dispatch(toggleCart());
  };

  const handleReferralClick = () => {
    handleMenuItemClick();
    if (!isAuthenticated) {
      toastManager.error('Please sign in to share your referral link');
      setTimeout(() => navigate(getAuthRedirectPath('/refer-and-earn')), 1000);
      return;
    }
    navigate('/refer-and-earn');
  };

  const handleRequestScriptClick = () => {
    handleMenuItemClick();
    if (!isAuthenticated) {
      toastManager.error(t('auth.signin_request_script_error', 'Please sign in to request a script'));
      setTimeout(() => navigate(getAuthRedirectPath('/script-requests')), 1000);
      return;
    }
    navigate('/script-requests');
  };

  const handlePointsHistoryClick = () => {
    handleMenuItemClick();
    setPointsHistoryOpen(true);
  };

  const handlePointsTopUpClick = () => {
    handleMenuItemClick();
    if (!isAuthenticated) {
      toastManager.error(t('auth.signin_topup_error', 'Please sign in to buy reward points'));
      setTimeout(() => navigate(getAuthRedirectPath('/profile')), 1000);
      return;
    }
    setPointsTopUpOpen(true);
  };

  const handleNotificationsClick = () => {
    handleMenuItemClick();
    navigate('/notifications');
  };

  const hasDynamicDownloads =
    Array.isArray(settings?.downloadItems) &&
    settings.downloadItems.some((item) => item && typeof item.link === 'string' && item.link.trim());

  const hasDownloadLinks = hasDynamicDownloads;

  const handleLanguageChange = (label) => {
    const code = LANGUAGE_MAP[label];
    if (!code) return;

    // Update local i18n state
    i18n.changeLanguage(code);

    // If authenticated, update database profile
    if (isAuthenticated) {
      dispatch(updateUserProfile({ language: label }));
    }
  };

  const userInitial = ((user?.displayName || user?.name || user?.email || 'U').charAt(0) || 'U').toUpperCase();
  const isMobileRouteActive = (path) => location.pathname === path;
  const mobileMenuSections = [
    {
      title: 'Account',
      items: [
        { type: 'link', to: '/profile', icon: User, label: t('header.my_profile') },
        { type: 'link', to: '/my-scripts', icon: FileCode, label: t('header.my_scripts', 'My Scripts') },
        { type: 'link', to: '/orders', icon: Package, label: t('header.my_orders') },
        { type: 'button', onClick: handlePointsTopUpClick, icon: Coins, label: t('header.topup_points', 'Buy Reward Points') },
      ],
    },
    {
      title: 'Store',
      items: [
        { type: 'link', to: '/wishlist', icon: Heart, label: t('header.my_wishlist'), badge: wishlistItemCount },
        { type: 'button', onClick: handleReferralClick, icon: Gift, label: t('header.refer_earn') },
        ...(hasDownloadLinks ? [{ type: 'link', to: '/downloads', icon: Download, label: t('common.downloads', 'Downloads') }] : []),
        { type: 'button', onClick: handleNotificationsClick, icon: Bell, label: t('common.notifications', 'Notifications'), badge: unreadNotifications },
      ],
    },
    {
      title: 'Support',
      items: [
        { type: 'button', onClick: handleRequestScriptClick, icon: Gamepad2, label: t('header.request_script') },
        { type: 'link', to: '/contact', icon: Send, label: t('common.contact') },
      ],
    },
  ];
  
  return (

    <>
      {/* 
        MOBILE TOP BAR (STATIC)
        - Scrolls away with the page
        - Contains Logo and Action Icons
      */}
      <div className={`md:hidden ${activeTheme === 'halloween' ? 'bg-[#0c0515] border-b border-orange-500/30 shadow-md' : activeTheme !== 'default' ? 'bg-primary-50 dark:bg-gray-950' : 'bg-white dark:bg-gray-950'}`}>
        <div className="container mx-auto px-3 py-2.5">
          <div className="flex items-center justify-between">
            {/* Logo Section (Mobile) */}
            <Link to="/" className="flex min-w-0 shrink items-center space-x-2 group">
              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl overflow-hidden bg-slate-950 shadow-sm ring-1 ring-black/10 dark:ring-white/10 group-hover:scale-105 transition-transform">
                <img src={pshLogo} alt="PSH" className="h-full w-full object-cover" />
              </div>
              <h1 className="hidden truncate whitespace-nowrap text-base font-black leading-tight text-gray-950 dark:text-white notranslate sm:block" translate="no">
                PSH Store
              </h1>
            </Link>

            {/* Mobile Actions */}
            <div className="flex items-center gap-1.5 shrink-0">
              {/* Festival Theme */}
              {adminTheme !== 'default' && (
                <button
                  onClick={() => dispatch(setActiveTheme(activeTheme === 'default' ? adminTheme : 'default'))}
                  className={`flex h-9 w-9 items-center justify-center rounded-2xl transition-all ${
                    activeTheme === 'halloween'
                      ? 'bg-orange-500/25 text-orange-400 border border-orange-500/80 shadow-[0_0_10px_rgba(249,115,22,0.45)] hover:bg-orange-500/40'
                      : activeTheme !== 'default'
                        ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20'
                        : 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900'
                  }`}
                >
                  {getFestivalIcon()}
                </button>
              )}

              {/* Theme Toggle */}
              <button
                onClick={() => dispatch(toggleTheme())}
                className={`flex h-9 w-9 items-center justify-center rounded-2xl transition ${
                  getThemeHeaderActionClasses()
                }`}
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>

              {/* Language Switcher (Mobile) */}
              <div className="relative">
                <button
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className={`flex h-9 w-9 items-center justify-center rounded-2xl transition ${
                    activeTheme === 'halloween'
                      ? 'bg-[#1a0c2c] text-orange-400 border border-orange-500/60 hover:bg-orange-900/60 hover:text-white shadow-[0_0_8px_rgba(249,115,22,0.3)]'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  <Globe className="w-5 h-5" />
                </button>
                <AnimatePresence>
                  {langMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 10 }}
                      className="absolute right-0 top-full z-[110] mt-2 max-h-[60vh] w-48 overflow-y-auto rounded-2xl border border-gray-100 bg-white py-2 shadow-xl dark:border-gray-800 dark:bg-gray-900"
                    >
                      {LANGUAGES.map((lang) => {
                        const code = LANGUAGE_MAP[lang];
                        return (
                          <button
                            key={lang}
                            onClick={() => {
                              handleLanguageChange(lang);
                              setLangMenuOpen(false);
                            }}
                            className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${i18n.language === code ? 'text-primary-600 font-bold bg-primary-50 dark:bg-primary-900/20' : 'text-gray-700 dark:text-gray-300'}`}
                          >
                            <span>{LANGUAGE_FLAGS[lang]} {lang}</span>
                            {i18n.language === code && <div className="w-1.5 h-1.5 rounded-full bg-primary-600" />}
                          </button>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Wishlist */}
              <button
                onClick={handleWishlistClick}
                className={`relative flex h-9 w-9 items-center justify-center rounded-2xl transition ${
                  getThemeHeaderActionClasses()
                }`}
              >
                <Heart className="w-5 h-5" />
                {activeTheme === 'halloween' && (
                  <span className="absolute -bottom-0.5 -end-0.5 pointer-events-none text-orange-400/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                    <HalloweenSpiderWebIcon className="w-3 h-3" />
                  </span>
                )}
                {wishlistItemCount > 0 && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </button>

              {/* Cart */}
              <button
                onClick={handleCartClick}
                className={`relative flex h-9 w-9 items-center justify-center rounded-2xl transition ${
                  getThemeHeaderActionClasses()
                }`}
              >
                <ShoppingCart className="w-5 h-5" />
                {activeTheme === 'halloween' && (
                  <span className="absolute -top-1 -start-1 pointer-events-none text-orange-500 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">
                    <HalloweenBatIcon className="w-3.5 h-3.5 animate-pulse" />
                  </span>
                )}
                {cartItemCount > 0 && (
                  <span className={`absolute -top-1 -end-1 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${activeTheme === 'halloween' ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-[0_0_8px_rgba(249,115,22,0.7)]' : 'bg-primary-600 dark:bg-white text-white dark:text-black'}`}>
                    {cartItemCount}
                  </span>
                )}
              </button>

              {/* User Profile */}
              <button
                onClick={isAuthenticated ? handleUserMenuToggle : () => navigate(getAuthRedirectPath())}
                className={`rounded-2xl p-0.5 transition ${activeTheme === 'halloween' ? 'hover:bg-orange-950/40' : 'hover:bg-gray-100 dark:hover:bg-gray-900'}`}
              >
                {isAuthenticated && user?.photoURL ? (
                  <img src={user.photoURL} alt="User" className={`h-8 w-8 rounded-2xl border object-cover ${activeTheme === 'halloween' ? 'border-orange-500/60 shadow-[0_0_8px_rgba(249,115,22,0.3)]' : 'border-gray-200 dark:border-gray-700'}`} />
                ) : (
                  <div className={`flex h-8 w-8 items-center justify-center rounded-2xl ${activeTheme === 'halloween' ? 'bg-[#1a0c2c] text-orange-400 border border-orange-500/60 shadow-[0_0_8px_rgba(249,115,22,0.3)]' : 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400'}`}>
                    <User className="w-4 h-4" />
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 
        MAIN STICKY HEADER 
        - Mobile: Search Bar Only
        - Desktop: Full Header (Logo + Search + Icons)
      */}
      <header className={`sticky top-0 z-50 border-b shadow-none transition-all duration-300 md:shadow-sm md:backdrop-blur-xl ${
        activeTheme !== 'default'
          ? 'border-white/10 bg-gray-950/85 md:bg-gray-950/80 backdrop-blur-xl shadow-lg'
          : 'border-transparent bg-white dark:border-transparent dark:bg-gray-950 md:border-gray-200/70 md:bg-white/95 md:dark:border-gray-800 md:dark:bg-gray-950/95'
      }`}>
        <div className="container mx-auto px-3 md:px-6">
          <div className="flex items-center gap-4 py-2.5 md:gap-6 md:py-3">

            {/* Desktop Logo (Hidden on Mobile) */}
            <Link to="/" className="hidden md:flex items-center space-x-2 rtl:space-x-reverse group shrink-0">
              <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl overflow-hidden bg-slate-950 shadow-md ring-1 ring-black/10 dark:ring-white/10 transition-transform duration-200 group-hover:scale-105">
                <img src={pshLogo} alt="PSH" className="h-full w-full object-cover" />
              </div>
              <div>
                <h1 className="text-xl font-black leading-tight text-gray-950 dark:text-white notranslate" translate="no">
                  PSH Store
                </h1>
              </div>
            </Link>

            {/* Search Bar (Visible on both Mobile and Desktop) */}
            <div className="w-full md:flex-1 md:max-w-2xl" ref={searchRef}>
              <div className="relative group">
                <Search className={`absolute start-3.5 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 transition-colors ${activeTheme !== 'default'
                  ? 'text-primary-500 dark:text-primary-400 group-focus-within:text-primary-600 dark:group-focus-within:text-primary-300'
                  : 'text-gray-400 group-focus-within:text-primary-500'
                  }`} />
                <input
                  type="text"
                  placeholder={t('header.search_scripts')}
                  value={searchQuery}
                  onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                  onFocus={() => setSearchFocus(true)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
                  className={`w-full rounded-2xl border ps-10 pe-12 py-2.5 text-sm font-semibold transition-all focus:ring-4 md:py-3 md:text-base ${
                    activeTheme !== 'default'
                      ? 'border-white/20 bg-white/10 text-white placeholder-white/50 focus:border-primary-400 focus:bg-white/15 focus:ring-primary-500/20 backdrop-blur-md shadow-inner'
                      : 'border-gray-200 bg-gray-100 text-gray-950 placeholder-gray-500 focus:border-gray-300 focus:bg-white focus:ring-gray-950/10 dark:border-gray-800 dark:bg-gray-900 dark:text-white dark:focus:border-gray-700 dark:focus:bg-gray-900 dark:focus:ring-white/10'
                  }`}
                />
                <button
                  onClick={() => handleSearch(searchQuery)}
                  className={`absolute end-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 shadow-sm opacity-0 transition-all scale-90 group-focus-within:scale-100 group-focus-within:opacity-100 ${activeTheme !== 'default'
                    ? 'bg-white dark:bg-primary-800 text-primary-600 dark:text-primary-100'
                    : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                    }`}
                >
                  <Search className="w-3.5 h-3.5" />
                </button>
                <AnimatePresence>
                  {searchFocus && searchQuery.trim() && (
                    <SearchDropdown
                      query={searchQuery}
                      onSelect={(product) => {
                        navigate(`/product/${product.id}`);
                        setSearchFocus(false);
                      }}
                      onViewAll={() => {
                        navigate('/products');
                        setSearchFocus(false);
                      }}
                    />
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Desktop Actions (Hidden on Mobile) */}
            <div className="hidden shrink-0 items-center gap-2 md:flex">
              {/* Festival Theme Toggle */}
              {adminTheme !== 'default' && (
                <button
                  onClick={() => dispatch(setActiveTheme(activeTheme === 'default' ? adminTheme : 'default'))}
                  className={`flex h-10 w-10 items-center justify-center rounded-2xl transition-all hover:scale-105 ${
                    activeTheme === 'halloween'
                      ? 'bg-orange-500/25 text-orange-400 border border-orange-500/80 shadow-[0_0_10px_rgba(249,115,22,0.45)] hover:bg-orange-500/40'
                      : activeTheme !== 'default'
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                {getFestivalIcon()}
                </button>
              )}

              {/* Theme Toggle */}
              <button
                onClick={() => dispatch(toggleTheme())}
                className={`flex h-10 w-10 items-center justify-center rounded-2xl transition-colors ${
                  getThemeHeaderActionClasses()
                }`}
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>

              {/* Language Switcher (Desktop) */}
              <div className="relative" ref={langMenuRef}>
                <button
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className={`flex h-10 w-10 items-center justify-center rounded-2xl transition-all ${
                    activeTheme === 'halloween'
                      ? 'bg-[#1a0c2c] text-orange-400 border border-orange-500/60 hover:bg-orange-900/60 hover:text-white shadow-[0_0_8px_rgba(249,115,22,0.3)]'
                      : langMenuOpen
                        ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  <Globe className="w-5 h-5" />
                </button>
                <AnimatePresence>
                  {langMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 top-full z-[110] mt-2 w-56 overflow-hidden rounded-3xl border border-gray-100 bg-white py-2 shadow-xl dark:border-gray-800 dark:bg-gray-900"
                    >
                      <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('profile.language_label')}</p>
                      </div>
                      <div className="max-h-[300px] overflow-y-auto py-1 custom-scrollbar">
                        {LANGUAGES.map((lang) => {
                          const code = LANGUAGE_MAP[lang];
                          return (
                            <button
                              key={lang}
                              onClick={() => {
                                handleLanguageChange(lang);
                                setLangMenuOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${i18n.language === code ? 'text-primary-600 font-bold bg-primary-50 dark:bg-primary-900/10' : 'text-gray-700 dark:text-gray-300'}`}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-base">{LANGUAGE_FLAGS[lang]}</span>
                                <span>{lang}</span>
                              </div>
                              {i18n.language === code && <div className="w-1.5 h-1.5 rounded-full bg-primary-600" />}
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <button
                onClick={handleWishlistClick}
                className={`relative flex h-10 w-10 items-center justify-center rounded-2xl transition-colors ${
                  getThemeHeaderActionClasses()
                }`}
              >
                <Heart className="w-5 h-5" />
                {activeTheme === 'halloween' && (
                  <span className="absolute -bottom-0.5 -end-0.5 pointer-events-none text-orange-400/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                    <HalloweenSpiderWebIcon className="w-3.5 h-3.5" />
                  </span>
                )}
                {wishlistItemCount > 0 && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </button>

              <button
                onClick={handleCartClick}
                className={`relative flex h-10 w-10 items-center justify-center rounded-2xl transition-colors ${
                  getThemeHeaderActionClasses()
                }`}
              >
                <ShoppingCart className="w-5 h-5" />
                {activeTheme === 'halloween' && (
                  <span className="absolute -top-1 -start-1 pointer-events-none text-orange-500 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">
                    <HalloweenBatIcon className="w-3.5 h-3.5 animate-pulse" />
                  </span>
                )}
                {cartItemCount > 0 && (
                  <span className={`absolute -top-1 -end-1 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${activeTheme === 'halloween' ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-[0_0_8px_rgba(249,115,22,0.7)]' : 'bg-primary-600 dark:bg-white text-white dark:text-black'}`}>
                    {cartItemCount}
                  </span>
                )}
              </button>

              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                {isAuthenticated ? (
                  <button
                    onClick={handleUserMenuToggle}
                    className={`ms-1 flex items-center gap-2 rounded-2xl border py-1 pe-3 ps-1 transition-colors ${
                      activeTheme === 'halloween'
                        ? 'border-orange-500/60 bg-[#1a0c2c] hover:border-orange-400 hover:bg-[#25123d] text-orange-200 shadow-[0_0_8px_rgba(249,115,22,0.25)]'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700 dark:hover:bg-gray-800'
                    }`}
                  >
                    {user?.photoURL ? (
                      <img src={user.photoURL} alt="User" className="h-8 w-8 rounded-2xl object-cover" />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-2xl bg-gray-100 dark:bg-gray-950">
                        <User className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </div>
                    )}
                    <div className="text-left hidden lg:block">
                      <p className="text-xs font-semibold text-gray-900 dark:text-white leading-none">
                        {user?.displayName?.split(' ')[0] || t('common.profile')}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-none mt-0.5">
                        {user?.rewardPoints ?? 0} {t('header.points')}
                      </p>
                    </div>
                    <ChevronDown className="w-3 h-3 text-gray-400" />
                  </button>
                ) : (
                  <Link
                    to={getAuthRedirectPath()}
                    className="ms-1 rounded-2xl bg-primary-600 px-4 py-2 text-sm font-black text-white transition-colors hover:bg-primary-700 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-100"
                  >
                    {t('common.sign_in')}
                  </Link>
                )}

                {/* Dropdown Menu (Desktop) */}
                <AnimatePresence>
                  {userMenuOpen && isAuthenticated && !isMobile && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.95 }}
                      className="absolute end-0 top-full z-[110] mt-2 w-72 overflow-hidden rounded-3xl border border-gray-100 bg-white py-2 shadow-xl dark:border-gray-800 dark:bg-gray-900"
                    >
                      <div className="border-b border-primary-100 bg-gradient-to-br from-white via-primary-50 to-amber-50 px-4 py-3 text-gray-950 dark:border-white/10 dark:bg-gray-950 dark:bg-none dark:text-white">
                        <p className="truncate font-bold text-gray-950 dark:text-white">
                          {user?.displayName || 'User'}
                        </p>
                        <p className="mb-2 truncate text-xs text-gray-500 dark:text-gray-300">
                          {user?.email}
                        </p>
                        <div className="flex items-center justify-between mt-2 gap-2">
                          <div className="flex items-center gap-2 rounded-xl bg-amber-100 px-2 py-1 text-xs font-bold text-amber-700 dark:bg-amber-400/15 dark:text-amber-100">
                            <Gift className="w-3 h-3" />
                            {t('header.points')}: {user?.rewardPoints ?? 0}
                          </div>
                          <button
                            onClick={handlePointsHistoryClick}
                            className="rounded-xl border border-primary-100 bg-white px-2 py-1 text-[10px] font-bold text-primary-700 transition-colors hover:bg-primary-50 dark:border-white/10 dark:bg-white/[0.08] dark:text-primary-100 dark:hover:bg-white/[0.14]"
                          >
                            {t('header.view_history')}
                          </button>
                        </div>
                      </div>

                      <div className="p-2">
                        <Link to="/profile" onClick={handleMenuItemClick} className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-colors">
                          <User className="w-4 h-4" /> {t('header.my_profile')}
                        </Link>
                        <Link to="/my-scripts" onClick={handleMenuItemClick} className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-colors">
                          <FileCode className="w-4 h-4" /> {t('header.my_scripts', 'My Scripts')}
                        </Link>
                        <Link to="/orders" onClick={handleMenuItemClick} className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-colors">
                          <Package className="w-4 h-4" /> {t('header.my_orders')}
                        </Link>
                        <Link to="/wishlist" onClick={handleMenuItemClick} className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-colors">
                          <Heart className="w-4 h-4" /> {t('header.my_wishlist')}
                        </Link>
                          <Link to="/downloads" onClick={handleMenuItemClick} className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-colors">
                            <Download className="w-4 h-4" /> {t('common.downloads', 'Downloads')}
                          </Link>
                        )}
                        <button onClick={handleNotificationsClick} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-colors">
                          <Bell className="w-4 h-4" /> {t('common.notifications', 'Notifications')}
                          {unreadNotifications > 0 && (
                            <span className="ms-auto min-w-5 px-1.5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                              {unreadNotifications}
                            </span>
                          )}
                        </button>
                        <button onClick={handleReferralClick} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-colors">
                          <Gift className="w-4 h-4" /> {t('header.refer_earn')}
                        </button>

                        <div className="h-px bg-gray-100 dark:bg-gray-800 my-1" />

                        <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl transition-colors">
                          <LogOut className="w-4 h-4" /> {t('common.logout')}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile User Menu Overlay */}
      <AnimatePresence>
        {userMenuOpen && isAuthenticated && isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[80]"
            onClick={() => setUserMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Profile Menu */}
      <AnimatePresence>
        {userMenuOpen && isAuthenticated && isMobile && (
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-0 left-0 z-[85] h-full w-[21rem] max-w-[88vw] overflow-hidden border-r border-primary-100 bg-white text-gray-950 shadow-2xl dark:border-white/10 dark:bg-gray-950 dark:text-white"
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-36 bg-gradient-to-br from-primary-500/10 via-accent-500/10 to-transparent dark:from-primary-500/30 dark:via-accent-500/25" />

            <div className="relative flex h-full flex-col">
              <div className="p-4 pb-3">
                <div className="mb-4 flex items-center justify-between">
                  <div className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-white/80 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-primary-700 shadow-sm dark:border-white/10 dark:bg-white/10 dark:text-primary-100 dark:shadow-none">
                    <Sparkles className="h-3.5 w-3.5 text-amber-300" />
                    PSH Account
                  </div>
                  <button
                    onClick={() => setUserMenuOpen(false)}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-primary-100 bg-white/80 text-gray-600 transition-colors hover:bg-white hover:text-gray-950 dark:border-white/10 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/20 dark:hover:text-white"
                    aria-label="Close menu"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="rounded-2xl border border-primary-100 bg-white/85 p-4 text-gray-950 shadow-sm backdrop-blur dark:border-white/10 dark:bg-white/[0.07] dark:text-white dark:shadow-xl">
                  <div className="flex items-start gap-3">
                    {user?.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt={user.displayName || user.email}
                        className="h-14 w-14 rounded-2xl border border-primary-100 object-cover dark:border-white/20"
                      />
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 via-accent-500 to-amber-400 text-xl font-black text-white shadow-lg shadow-primary-900/30">
                        {userInitial}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-base font-bold leading-tight text-gray-950 dark:text-white">
                        {user?.displayName || user?.name || 'User'}
                      </h3>
                      <p className="mt-1 truncate text-xs font-medium text-gray-500 dark:text-gray-300">
                        {user?.email}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-700 dark:bg-amber-400/20 dark:text-amber-200">
                          <Gift className="h-3.5 w-3.5" />
                          RP {user?.rewardPoints ?? 0}
                        </div>
                        <button
                          onClick={handlePointsHistoryClick}
                          className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-1 text-[11px] font-bold text-primary-700 transition-colors hover:bg-primary-100 dark:bg-white/10 dark:text-primary-100 dark:hover:bg-white/20"
                        >
                          {t('header.view_history')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-2.5 pb-3 no-scrollbar">
                {mobileMenuSections.map((section) => (
                  <div key={section.title} className="mb-3">
                    <p className="px-2 pb-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-gray-500">
                      {section.title}
                    </p>
                    <div className="space-y-0.5">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const active = item.to ? isMobileRouteActive(item.to) : false;
                        const content = (
                          <>
                            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition-colors ${
                              active
                                ? 'bg-white text-primary-700 dark:text-gray-950'
                                : 'bg-gray-100 text-gray-500 group-hover:bg-primary-50 group-hover:text-primary-700 dark:bg-white/[0.08] dark:text-gray-300 dark:group-hover:bg-white/[0.12] dark:group-hover:text-white'
                            }`}>
                              <Icon className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 flex-1 truncate text-left font-semibold">{item.label}</span>
                            {item.badge > 0 && (
                              <span className="min-w-5 rounded-full bg-red-500 px-1.5 py-0.5 text-center text-[10px] font-black leading-none text-white">
                                {item.badge}
                              </span>
                            )}
                            <ChevronRight className={`h-4 w-4 transition-transform group-hover:translate-x-0.5 ${active ? 'text-white' : 'text-gray-400 dark:text-gray-600'}`} />
                          </>
                        );
                        const itemClass = `group flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-sm transition-all ${
                          active
                            ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-lg shadow-primary-900/30'
                            : 'text-gray-700 hover:bg-primary-50 hover:text-primary-700 dark:text-gray-300 dark:hover:bg-white/[0.07] dark:hover:text-white'
                        }`;

                        return item.type === 'link' ? (
                          <Link
                            key={item.label}
                            to={item.to}
                            onClick={handleMenuItemClick}
                            className={itemClass}
                            aria-current={active ? 'page' : undefined}
                          >
                            {content}
                          </Link>
                        ) : (
                          <button
                            key={item.label}
                            onClick={item.onClick}
                            className={itemClass}
                          >
                            {content}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                <div className="mt-1 border-t border-gray-100 pt-2.5 dark:border-white/10">
                  <button
                    onClick={handleLogout}
                    className="group flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 dark:text-red-200 dark:hover:bg-red-500/10 dark:hover:text-red-100"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-red-50 text-red-500 group-hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:group-hover:bg-red-500/20">
                      <LogOut className="h-4 w-4" />
                    </span>
                    <span className="flex-1 text-left">{t('common.logout')}</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Modals */}
      <AnimatePresence>
        {pointsHistoryOpen && (
          <PointsHistoryModal
            isOpen={pointsHistoryOpen}
            onClose={() => setPointsHistoryOpen(false)}
            userId={user?.uid}
          />
        )}
        {pointsTopUpOpen && (
          <PointsTopUpModal
            isOpen={pointsTopUpOpen}
            onClose={() => setPointsTopUpOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;

